import React, { useEffect, useRef, useState } from 'react';
import './custom-progress-bar.css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlay, faPause, faExpand, faCompress } from '@fortawesome/free-solid-svg-icons';

const formatTime = (seconds) => {
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${mins}:${secs < 10 ? '0' : ''}${secs}`;
};

const CustomProgressBar = ({
  progress,
  onSeek,
  isPlaying,
  onTogglePlay,
  currentTime = 0,
  duration = 0,
  videoRef,
}) => {
  const barRef = useRef(null);
  const [isDragging, setIsDragging] = useState(false);
  const [dragProgress, setDragProgress] = useState(progress);
  const [isHovering, setIsHovering] = useState(false);
  const [isFullScreen, setIsFullScreen] = useState(false);

  useEffect(() => {
    if (!isDragging) setDragProgress(progress);
  }, [progress, isDragging]);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.code === 'Space') {
        e.preventDefault();
        onTogglePlay?.();
      }
    };
    const handleFullScreenChange = () => {
      setIsFullScreen(!!document.fullscreenElement);
    };

    window.addEventListener('keydown', handleKeyDown);
    document.addEventListener('fullscreenchange', handleFullScreenChange);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      document.removeEventListener('fullscreenchange', handleFullScreenChange);
    };
  }, [onTogglePlay]);

  const handleSeek = (e) => {
    const rect = barRef.current.getBoundingClientRect();
    const clickX = e.clientX - rect.left;
    const percent = clickX / rect.width;
    onSeek?.(percent);
  };

  const handleDrag = (e) => {
    const rect = barRef.current.getBoundingClientRect();
    const moveX = e.clientX - rect.left;
    const maxPercent = progress / 100;
    const percent = Math.max(0, Math.min(moveX / rect.width, maxPercent));
    setDragProgress(percent * 100);
  };

  const handleMouseUp = () => {
    setIsDragging(false);
    onSeek?.(dragProgress / 100);
  };

  const toggleFullScreen = () => {
    const videoEl = videoRef?.current;
    if (!videoEl) return;

    if (!document.fullscreenElement) {
      videoEl.requestFullscreen?.();
    } else {
      document.exitFullscreen?.();
    }
  };

  return (
    <div
      className="custom-player-wrapper"
      onMouseEnter={() => setIsHovering(true)}
      onMouseLeave={() => setIsHovering(false)}
    >
      <button className="play-fab" onClick={onTogglePlay}>
        <FontAwesomeIcon icon={isPlaying ? faPause : faPlay} />
      </button>

      <div
        className="custom-progress-container"
        ref={barRef}
        onClick={handleSeek}
        onMouseDown={(e) => {
          setIsDragging(true);
          handleDrag(e);
        }}
        onMouseMove={(e) => isDragging && handleDrag(e)}
        onMouseUp={handleMouseUp}
        onMouseLeave={() => isDragging && setIsDragging(false)}
      >
        <div
          className="custom-progress-fill"
          style={{ width: `${dragProgress}%` }}
        />
        <div
          className={`custom-progress-thumb ${isHovering ? 'hover' : ''}`}
          style={{ left: `${dragProgress}%` }}
        >
          <div className="custom-progress-thumb-inner" />
        </div>
      </div>

      <div className="custom-controls-right">
        <span className="custom-time-display">
          {formatTime(currentTime)} / {formatTime(duration)}
        </span>
        <button className="fullscreen-btn" onClick={toggleFullScreen}>
          <FontAwesomeIcon icon={isFullScreen ? faCompress : faExpand} />
        </button>
      </div>
    </div>
  );
};

export default CustomProgressBar;
